import { useApiClient, toast } from '@startupquickstart/frontend';
import { useMutation } from '@tanstack/react-query';

interface GenerateBulkTourTranslationsParams {
  tourId: string;
  targetLanguageIds: string[];
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
}

interface GenerateBulkTourTranslationsResponse {
  success: boolean;
  error?: string;
  data?: { jobId: string };
}

/**
 * Kick off a bulk translation + voiceover job for a tour. Returns the
 * `jobId` so callers can subscribe to progress via `useTourTranslationJobSocket`.
 */
export function useGenerateBulkTourTranslations() {
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: async ({
      tourId,
      targetLanguageIds,
      voice = 'alloy',
    }: GenerateBulkTourTranslationsParams) => {
      const response = (await apiClient.post(
        `/tours/${tourId}/generate-bulk-translations`,
        { targetLanguageIds, voice },
      )) as GenerateBulkTourTranslationsResponse;

      if (!response || response.success === false || !response.data) {
        throw new Error(response?.error || 'Failed to start bulk translation');
      }
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start bulk translation');
    },
  });
}
